import { toast } from 'sonner'
import { useCallback, useMemo } from 'react'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from './useSessionStorage'
import { useMutation } from '@tanstack/react-query'
import { ENDPOINTS } from '@/util'
import { clearAiChatBrowserStorage } from '@/utils/aiChatStorage'
import { leaveBlockedTab } from '@/utils/tabSession'

const flushRedis = async () => {
  const url = `${ENDPOINTS.DOTNET}/rest/support/flush-redis`
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (response.ok) {
    return response.json()
  }
  throw new Error('Failed to flush redis')
}

const setRedis = async (key: string, value: string) => {
  const url = `${ENDPOINTS.DOTNET}/rest/support/set-redis`
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key, value }),
  })
  if (response.ok) {
    return response.json()
  }
  throw new Error('Failed to set redis')
}

export function useLogout() {
  const { postAuthenticationApiLogout } = useRestClient()
  const { mutate: Logout } = postAuthenticationApiLogout.useMutation()
  const [sub] = useSessionStorage('sub', '')
  const [token] = useSessionStorage('token', '')
  const [isRefreshing] = useSessionStorage('isRefreshing', false)

  const logoutHandler = useCallback(() => {
    Logout(
      {},
      {
        onSuccess: () => {
          clearAiChatBrowserStorage()
          sessionStorage.clear()
          window.location.replace(`/?authStatus=logout-success`)
        },
        onError: () => {
          toast.warning('Logout failed', {
            id: 'logout-failed',
            duration: 500,
          })
          // still clear everything, logout can fail it token is already old etc
          clearAiChatBrowserStorage()
          sessionStorage.clear()
          window.location.replace(`/?authStatus=logout-failed`)
        },
      }
    )
  }, [Logout])

  const logoutWithBeacon = useCallback(async () => {
    if (sub && token && !isRefreshing) {
      logoutHandler()
    }
  }, [isRefreshing, logoutHandler, sub, token])

  /** Leave a blocked duplicate tab (close when allowed, else home with ?leftTab=1). */
  const dismissLocalSession = useCallback(() => {
    leaveBlockedTab()
  }, [])

  return useMemo(
    () => ({ logoutHandler, logoutWithBeacon, dismissLocalSession }),
    [logoutHandler, logoutWithBeacon, dismissLocalSession]
  )
}

export function useFlushRedis() {
  return useMutation({
    mutationFn: () => flushRedis(),
    onSuccess: () => {
      toast.info('Redis Flushed - You have been logged out.')
    },
    onError: () => {
      toast.warning('Redis flush failed')
    },
    onSettled: () => {
      clearAiChatBrowserStorage()
      sessionStorage.clear()
      window.location.replace(`/?authStatus=logout-success`)
    },
  })
}

export function useSetRedis() {
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => setRedis(key, value),
    onSuccess: () => {
      toast.info('Redis value set')
    },
    onError: () => {
      toast.warning('Redis set value failed')
    },
  })
}

export function useRefreshExternalTokens() {
  const { postSupportApiRefreshExternalTokens } = useRestClient()
  const { mutate } = postSupportApiRefreshExternalTokens.useMutation()

  return {
    mutate: () => {
      mutate(
        {},
        {
          onSuccess: () => {
            toast.info('External tokens regenerated successfully')
          },
          onError: () => {
            toast.warning('Failed to regenerate external tokens')
          },
        }
      )
    },
  }
}
